import { useState, useEffect } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Grid,
  IconButton
} from '@mui/material';
import Iconify from 'src/components/iconify';
import { useProfileContext } from 'src/components/profile/context';
import html2canvas from 'html2canvas';
import { formatCurrency } from '../../risk-register/feching';

const PayrollSlipExact = ({ payrollData, employeeData }) => {
  const [payslips, setPayslips] = useState([]);
  const { userProfile } = useProfileContext();
  
  useEffect(() => {
    if (payrollData && employeeData) {
      const formattedPayslips = employeeData?.map(employee => {
        const entries = employee.time_sheet?.weekly_time_sheet?.flatMap(day => day.entries) || [];
        const firstEntry = entries[0] || {};
        const regularTimeRate = firstEntry.trade_worker_title?.regular_time_rate || 0;
        const overtimeRate = firstEntry.trade_worker_title?.over_time_rate || 0;
        const tradeWorkTitle = firstEntry.trade_worker_title?.name || '';
        const code = firstEntry.trade_worker_title?.title_code || '';
        
        // Get values directly from API
        const totalHours = parseFloat(employee.time_sheet?.grand_total_hours || 0);
        const totalLaborCost = parseFloat(employee.time_sheet?.grand_total_labor_cost || 0);
        const taxDetails = employee.payroll_tax_details?.[0] || {};
        
        // Use API values directly without calculations
        const grossWages = totalLaborCost;
        const netPay = parseFloat(taxDetails.net_pay || 0);
        const federalTax = parseFloat(taxDetails.federal_income_tax || 0);
        const socialSecurity = parseFloat(taxDetails.social_security_employee || 0);
        const medicare = parseFloat(taxDetails.medicare_employee || 0);
        const stateTax = parseFloat(taxDetails.state_income_tax || 0); 
        const localTax = parseFloat(taxDetails.local_income_tax || 0);
        const stateDisability = 0;
        
        // YTD values directly from API
        const ytdHours = parseFloat(employee.time_sheet?.ytd_hours || 0);
        const ytdLaborCost = parseFloat(employee.time_sheet?.ytd_labor_cost || 0);
        const ytdFederalIncomeTax = parseFloat(employee.time_sheet?.yt_total_federal_income_tax || 0);
        const ytdSocialSecurityTax = parseFloat(employee.time_sheet?.yt_social_security_tax || 0);
        const ytdMedicareTax = parseFloat(employee.time_sheet?.yt_medicare_tax || 0);
        const netPayTotal = parseFloat(employee.time_sheet?.net_pay_total || 0);
        const totalWithHoldings = parseFloat(employee.time_sheet?.total_with_holdings || 0);
        
        // Calculate overtime hours
        let overtimeHours = 0;
        entries.forEach(entry => {
          if ((entry.total_time_hours || 0) > 8) {
            overtimeHours += entry.total_time_hours - 8;
          }
        });
        
        const standardHours = totalHours - overtimeHours;
        const standardPay = totalLaborCost - (overtimeHours * overtimeRate);
        const overtimePay = overtimeHours * overtimeRate;
      
        const withholdingTypes = {
          "Federal Income Tax (FITW)": federalTax,
          "Social Security Tax (OASDI)": socialSecurity,
          "Medicare Tax (HI)": medicare,
          "Additional Medicare Tax": 0,
          "State Income Tax Withholding": stateTax,
          "State Disability Insurance (SDI)": stateDisability,
          "State Unemployment Insurance (SUI)": 0,
          "Local Income Tax": localTax,
          "Commuter / Transit Taxes": 0
        }
        
        // All deduction types 
        const retirement = parseFloat(employee.retirement_contributions || 0);
        const healthInsurance = parseFloat(employee.health_insurance || 0);
        const healthSavings = parseFloat(employee.health_saving_account || 0);
        const roth401k = parseFloat(employee.roth_401_k || 0);
        const additionalInsurance = parseFloat(employee.additional_insurance || 0);
        const unionDues = parseFloat(employee.union_dues || 0);
        const charitableContributions = parseFloat(employee.charitable_contributions || 0);
        const loanRepayments = parseFloat(employee.loan_repayments || 0);
        const qualifiedTransportation = parseFloat(employee.qualified_transportation || 0);
        const groupLifeInsurance = parseFloat(employee.group_life_insurance || 0);
        
        const deductionTypes = {
          "Retirement Plan Contributions": retirement,
          "Health Insurance Premiums": healthInsurance,
          "Health Savings Account (HSA)": healthSavings,
          "Flexible Spending Account (FSA)": 0,
          "Qualified Transportation": qualifiedTransportation,
          "Group Life / Disability Insurance": groupLifeInsurance,
          "Roth 401(k) / Roth 403(b)": roth401k,
          "Additional Insurance": additionalInsurance,
          "Union Dues / Professional Fees": unionDues,
          "Charitable Contributions": charitableContributions,
          "Loan Repayments": loanRepayments,
          "401K": 0
        }
        // const totalDeduction = Object.values(deductionTypes).reduce((sum, val) => sum + val, 0);
        const totalDeduction =  0;
        return {
          employee: {
            id: employee.id || '',
            name: `${employee.first_name || ''} ${employee.last_name || ''}`,
            address: `${employee.address1 || ''} ${employee.city || ''} ${employee.state_id || ''}`,
            workTitle: tradeWorkTitle|| '',
            code: code|| '',
            companyId: employee.parent_id || '',
            chartOfAccount: '5-040', // Default value
            paymentOption: employee.payment_method || payrollData.payment_method || 'Direct Deposit',
            payPeriod: payrollData.payroll_period || 'weekly',
            tableTop: [
              payrollData.payroll_number || '',
              employee.parent_id || '',
              '5-040', // Chart of Account
              employee.payment_method || payrollData.payment_method || 'Direct Deposit',
              employee.first_name || '',
              employee.last_name || '',
              employee.id || '',
              employee.social_security_number ? `${employee.social_security_number}` : '',
              'Main', // Home Department
              employee.skillset ||tradeWorkTitle|| '',
              employee.code ||code|| ''
            ]
          },
          earnings: [
            ['Regular Hours', standardHours.toFixed(1), `$${regularTimeRate.toFixed(2)}`, formatCurrency(standardPay), ytdHours.toFixed(1), formatCurrency(ytdLaborCost)],
            ['Overtime', overtimeHours.toFixed(1), `$${overtimeRate.toFixed(2)}`, formatCurrency(overtimePay), '', ''],
            ['Bonus', '', '', '$0.00', '', '$0.00']
          ],
          withholdings: Object.entries(withholdingTypes).map(([desc, amount]) => [
            desc, 
            '', 
            formatCurrency(amount), 
            desc === "Federal Income Tax (FITW)" ? formatCurrency(ytdFederalIncomeTax) :
            desc === "Social Security Tax (OASDI)" ? formatCurrency(ytdSocialSecurityTax) :
            desc === "Medicare Tax (HI)" ? formatCurrency(ytdMedicareTax) : 
            formatCurrency(amount)
          ]),
          deductions: Object.entries(deductionTypes).map(([desc, amount]) => [
            desc,
            formatCurrency(amount),
            formatCurrency(amount)
          ]),
          summary: {
            totalHours: [totalHours.toFixed(1), ytdHours.toFixed(1)],
            grossEarnings: [formatCurrency(grossWages), formatCurrency(ytdLaborCost)],
            totalWithholdings: [formatCurrency(totalWithHoldings), formatCurrency(totalWithHoldings)],
            totalDeductions: [formatCurrency(totalDeduction), formatCurrency(totalDeduction)],
            netPay: [formatCurrency(netPay), formatCurrency(netPayTotal)],
            payPeriod: payrollData.payroll_period || 'weekly'
          }
        };
      }) || [];
      setPayslips(formattedPayslips);
    }
  }, [payrollData, employeeData]);
  
  const headerStyle = {
    fontWeight: 'bold',
    fontSize: '12px',
    backgroundColor: '#f9f9f9',
    border: '1px solid #ccc',
    padding: '6px',
  };

  const cellStyle = {
    border: '1px solid #ccc',
    padding: '4px',
    fontSize: '11px',
  };
  
  const downloadImage = () => {
    const element = document.getElementById('payroll-slip'); // Get the element by id
    html2canvas(element).then((canvas) => {
      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = 'payroll-slip.png';
      link.click(); 
    });
  };
  
  return (
    <Paper elevation={1} style={{ padding: '0px' }}>
      {payslips.map((slip, index) => (
        <Box key={index} mb={4}>
          <Box mt={0} display="flex" justifyContent="flex-end">
            <IconButton onClick={downloadImage}>
              <Iconify icon="material-symbols:download" />
            </IconButton>
          </Box>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<Iconify icon="eva:arrow-ios-downward-fill" />}>
              <Typography variant="subtitle1">Pay Stub {slip.employee.name}</Typography>
            </AccordionSummary>
            <AccordionDetails id="payroll-slip">
              <Container maxWidth="md" style={{ padding: '20px' }}>
                <Grid container spacing={1}>
                  <Grid item xs={4}>
                    <Typography fontWeight="bold" variant="h6">{userProfile?.company_name || ""}</Typography>
                    <Typography variant="body2">{userProfile?.address} {userProfile?.city}</Typography>
                    <Typography variant="body2">{userProfile?.country?.name} {userProfile?.state?.name} {userProfile?.zip}</Typography>
                    <Typography variant="body2">{userProfile?.phone}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2"><b>Employee Id:</b> {slip.employee.id}</Typography>
                    <Typography variant="body2"><b>Employee Name:</b> {slip.employee.name}</Typography>
                    <Typography variant="body2"><b>Address:</b> {slip.employee.address}</Typography>
                    <Typography variant="body2"><b>Work Title:</b> {slip.employee.workTitle}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" align="right"><b>Company Id:</b> {slip.employee.companyId}</Typography>
                    <Typography variant="body2" align="right"><b>Charts of Account:</b> {"5-040"}</Typography>
                    <Typography variant="body2" align="right"><b>Payment Option:</b> {slip.employee.paymentOption}</Typography>
                    <Typography variant="body2" align="right"><b>Pay Period:</b> {slip.employee.payPeriod}</Typography>
                  </Grid>
                </Grid>
                <Typography variant="subtitle1" align="end" sx={{ fontWeight: 'bold', my: 2 }}>***NON-NEGOTIABLE***</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {['Check No', 'Company Id', 'Chart of Account', 'Payment Option', 'Employee First Name', 'Last Name', 'Employee Id', 'Soc Sec', 'Home Department', 'Work Title', 'Code'].map((col) => (
                          <TableCell key={col} style={headerStyle}>{col}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        {slip.employee.tableTop.map((val, idx) => (
                          <TableCell key={idx} style={cellStyle}>{val}</TableCell>
                        ))}
                      </TableRow>
                    </TableBody>
                  </Table>

                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell colSpan={6} align="center" style={headerStyle}>EARNINGS</TableCell>
                        <TableCell colSpan={4} align="center" style={headerStyle}>WITH HOLDINGS</TableCell>
                        <TableCell colSpan={3} align="center" style={headerStyle}>DEDUCTIONS</TableCell>
                      </TableRow>
                      <TableRow>
                        {['Descriptions', 'hrs/Unit', 'Rate($)', 'This Period ($)', 'YTD Hrs', 'YTD ($)', 'Descriptions', 'Filing Status', 'This Period ($)', 'YTD ($)', 'Descriptions', 'This Period ($)', 'YTD ($)'].map((col) => (
                          <TableCell key={col} style={headerStyle}>{col}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {slip.earnings.map((earning, i) => (
                        <TableRow key={`e${i}`}>
                          {earning.map((val, j) => (
                            <TableCell key={j} style={cellStyle}>{val}</TableCell>
                          ))}
                          {slip.withholdings[i] && slip.withholdings[i].map((val, j) => (
                            <TableCell key={`w${j}`} style={cellStyle}>{val}</TableCell>
                          ))}
                          {slip.deductions[i] && slip.deductions[i].map((val, j) => (
                            <TableCell key={`d${j}`} style={cellStyle}>{val}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                      <TableRow>
                        {['Total Hours', slip.summary.totalHours[0], '', '', slip.summary.totalHours[1], 'YTD Labor Cost', '', '', '', '', '', '', ''].map((val, i) => (
                          <TableCell key={i} style={cellStyle}>{i === 5 ? slip.summary.grossEarnings[1] : val}</TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        {['Gross Earnings', '', '', slip.summary.grossEarnings[0], '', slip.summary.grossEarnings[1], 'Total Withholdings', '', slip.summary.totalWithholdings[0], slip.summary.totalWithholdings[1]].map((val, i) => (
                          <TableCell key={i} style={cellStyle}><b>{val}</b></TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        {['Net Pay', '', '', slip.summary.netPay[0], '', slip.summary.netPay[1], 'Pay Period', '', slip.summary.payPeriod, '', '', '', ''].map((val, i) => (
                          <TableCell key={i} style={cellStyle}>{val}</TableCell>
                        ))}
                      </TableRow>
                      {/* <TableRow style={{ backgroundColor: '#f0f0f0' }}>
                        {['YTD Summary', '', '', '', '', '', '', '', '', '', '', '', ''].map((val, i) => (
                          <TableCell key={i} style={{...cellStyle, fontWeight: 'bold'}}>{val}</TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        {[
                          'YTD Hours', 
                          slip.summary.totalHours[1], 
                          'YTD Labor Cost', 
                          slip.summary.grossEarnings[1], 
                          'YTD Fed Tax', 
                          formatCurrency(slip.withholdings[0][3].replace('$', '')),
                          'YTD SS Tax', 
                          formatCurrency(slip.withholdings[1][3].replace('$', '')),
                          'YTD Medicare', 
                          formatCurrency(slip.withholdings[2][3].replace('$', '')),
                          'Net Pay Total', 
                          slip.summary.netPay[1],
                          'Total Withholdings'
                        ].map((val, i) => (
                          <TableCell key={i} style={cellStyle}>{val}</TableCell>
                        ))}
                      </TableRow> */}
                    </TableBody>
                  </Table>
                  <Box
                    mt={2}
                    px={3}
                    py={2}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    borderTop="1px solid #ccc"
                  >
                    <Box>
                      <img src="/logo/logo_single.png" alt="eZeLogs Logo" style={{ height: 40 }} />
                    </Box>
                    <Box>
                      <img src="/logo/scan.png" alt="QR Code" style={{ height: 60 }} />
                    </Box>
                  </Box>
                </TableContainer>
              </Container>
            </AccordionDetails>
          </Accordion>
          <Divider />
        </Box>
      ))}
    </Paper>
  );
};

export default PayrollSlipExact;